import { FC, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BarChartBlock as BarChartBlockType } from '@/types/blocks';
import { Text } from '@/components/Typography';
import { seriesColor } from './themeColors';
import { useDarkMode } from '@/hooks/useDarkMode';
import { ExpandDrawer } from './ExpandDrawer';
import { formatTick } from './formatTick';
import { colors } from '@/theme/colors';

export const BarChartBlock: FC<{ block: BarChartBlockType }> = ({ block }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isDark = useDarkMode();
  const axisColor = isDark ? colors.grey.hover.dark : colors.grey.hover.light;
  const gridColor = isDark ? colors.grey.main.light : colors.peach[1];
  const isHorizontal = block.horizontal ?? false;

  const data = useMemo(
    () =>
      block.x_labels.map((label, i) => {
        const point: Record<string, string | number> = { label };
        block.series.forEach(s => {
          point[s.name] = s.values[i] ?? 0;
        });
        return point;
      }),
    [block]
  );

  const categoryKey = 'label';
  const layout = isHorizontal ? 'vertical' : 'horizontal';

  const renderChart = (height: number | `${number}%`) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout={layout} margin={{ top: 8, right: 16, left: 12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        {isHorizontal ? (
          <>
            <YAxis
              type="category"
              dataKey={categoryKey}
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              width={100}
            />
            <XAxis
              type="number"
              tickFormatter={formatTick}
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              label={
                block.y_axis_label
                  ? {
                      value: block.y_axis_label,
                      position: 'insideBottom',
                      offset: -4,
                      fill: axisColor,
                      fontSize: 12,
                    }
                  : undefined
              }
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={categoryKey}
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
            />
            <YAxis
              width={56}
              tickFormatter={formatTick}
              tick={{ fill: axisColor, fontSize: 12 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              label={
                block.y_axis_label
                  ? { value: block.y_axis_label, angle: -90, position: 'insideLeft', fill: axisColor, fontSize: 12 }
                  : undefined
              }
            />
          </>
        )}
        <Tooltip
          formatter={value => formatTick(value)}
          contentStyle={{
            backgroundColor: isDark ? colors.black.light : colors.grey.sky.light,
            border: `1px solid ${gridColor}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          labelStyle={{ color: isDark ? colors.grey.main.dark : colors.grey.black.light }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {block.series.map((s, i) => (
          <Bar
            key={s.name}
            dataKey={s.name}
            fill={seriesColor(i, s.color, isDark)}
            stackId={block.stacked ? (s.stack ?? 'default') : undefined}
            radius={block.stacked ? undefined : [4, 4, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        {block.title ? (
          <Text variant="text-md-bold" className="text-grey-black dark:text-grey-main-dark">
            {block.title}
          </Text>
        ) : (
          <span />
        )}
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-md p-1.5 text-grey-medium transition-colors hover:bg-peach hover:text-grey-black dark:hover:bg-grey-sky-dark"
          aria-label="Expand chart"
          title="Expand chart"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>
      {renderChart(Math.max(320, data.length * 40))}
      <ExpandDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={block.title ?? 'Bar chart'}>
        <div style={{ height: 'min(calc(100vh - 140px), 800px)' }}>{renderChart('100%')}</div>
      </ExpandDrawer>
    </div>
  );
};
